<script setup>
/**
 * @fileoverview Preview canvas for Custom Mosaics.
 *
 * Draws the selected pattern as SVG in inch units (viewBox = sheet size ×
 * repeat), so the aspect ratio is always honored. The sheet background is the
 * grout color and every tile is inset by half the 3/16" joint, so adjacent
 * sheets in the field view meet at a full joint.
 *
 * Painting: with a glaze selected the cursor becomes a paint bucket. Pointer
 * hits are resolved mathematically against the tile rectangles (wrapped back to
 * the base sheet), so clicks on grout or on any repeated copy paint the
 * underlying base tile. Click-and-drag paints a stroke (one undo step).
 *
 * @component
 */
import { ref, computed, onBeforeUnmount } from 'vue';
import { useMosaicStore, MIN_REPEAT, MAX_REPEAT } from '../stores/mosaicStore';
import {
    GROUT_WIDTH_IN,
    UNPAINTED_FILL,
    UNPAINTED_STROKE,
    hitTestTile,
    formatSize,
} from '../utils/patternData.js';
import { paintBucketCursor } from '../utils/paintBucketCursor.js';
import ShareDesign from './Shared/ShareDesign.vue';
import { trackPaint, trackReset, trackRepeatChange } from '../utils/trackingService';
import {
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    ArrowPathIcon,
    MinusIcon,
    PlusIcon,
    PaintBrushIcon,
    Squares2X2Icon,
    XMarkIcon,
} from '@heroicons/vue/24/outline';

const store = useMosaicStore();

/** Sampling step (inches) when interpolating a fast drag between pointer events */
const DRAG_SAMPLE_IN = 0.25;

const sheetId = `cm-sheet-${Math.random().toString(36).slice(2, 9)}`;

const svgEl = ref(null);
const hoverTileId = ref(null);
const needsColorHint = ref(false);
let hintTimer = null;
let painting = false;
let lastPaintedId = null;
let lastPoint = null;

const pattern = computed(() => store.selectedPattern);
const half = GROUT_WIDTH_IN / 2;

const fieldWidth = computed(() => (pattern.value?.width || 0) * store.repeat);
const fieldHeight = computed(() => (pattern.value?.height || 0) * store.repeat);

/** Offsets of every sheet copy in the field, base sheet first */
const copies = computed(() => {
    const p = pattern.value;
    if (!p) return [];
    const out = [];
    for (let j = 0; j < store.repeat; j++) {
        for (let i = 0; i < store.repeat; i++) out.push({ x: i * p.width, y: j * p.height });
    }
    return out;
});

const tilesById = computed(() => new Map((pattern.value?.tiles || []).map((t) => [t.id, t])));

/** Tiles to outline on hover: the hovered tile, or its whole color group in fill mode */
const highlightTiles = computed(() => {
    const id = hoverTileId.value;
    const p = pattern.value;
    if (!id || !p || !store.activeColor) return [];
    if (store.paintMode !== 'fill') return [tilesById.value.get(id)].filter(Boolean);
    const target = store.colors[id]?.name || null;
    return p.tiles.filter((t) => (store.colors[t.id]?.name || null) === target);
});

const hoverTile = computed(() => tilesById.value.get(hoverTileId.value) || null);

const cursorStyle = computed(() =>
    store.activeColor ? paintBucketCursor(store.activeColor.hex) : 'default',
);

function tileLabel(t) {
    const c = store.colors[t.id];
    return `Tile ${t.id}, ${formatSize(t.size)}, ${c ? c.name : 'unpainted'}`;
}

function showNeedsColorHint() {
    needsColorHint.value = true;
    store.setOpenStep(2);
    clearTimeout(hintTimer);
    hintTimer = setTimeout(() => (needsColorHint.value = false), 3000);
}

/** Converts a pointer event to sheet coordinates (inches). */
function toSheetPoint(e) {
    const svg = svgEl.value;
    const ctm = svg?.getScreenCTM?.();
    if (!ctm) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
}

function tileAt(point) {
    return point ? hitTestTile(pattern.value, point.x, point.y, store.repeat) : null;
}

function paintAt(point) {
    const t = tileAt(point);
    if (t && t.id !== lastPaintedId) {
        lastPaintedId = t.id;
        store.paintTile(t.id);
    }
}

function onPointerDown(e) {
    if (e.button !== 0 || !pattern.value) return;
    const point = toSheetPoint(e);
    const t = tileAt(point);
    if (!t) return;
    if (!store.activeColor) {
        showNeedsColorHint();
        return;
    }
    e.preventDefault();
    svgEl.value?.setPointerCapture?.(e.pointerId);
    painting = true;
    lastPaintedId = null;
    lastPoint = point;
    store.beginStroke();
    paintAt(point);
}

function onPointerMove(e) {
    const point = toSheetPoint(e);
    const t = tileAt(point);
    hoverTileId.value = t?.id || null;
    if (!painting || store.paintMode !== 'tile' || !point) return;

    // Sample between pointer events so a fast drag doesn't skip tiles
    const dx = point.x - lastPoint.x;
    const dy = point.y - lastPoint.y;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / DRAG_SAMPLE_IN));
    for (let s = 1; s <= steps; s++) {
        paintAt({ x: lastPoint.x + (dx * s) / steps, y: lastPoint.y + (dy * s) / steps });
    }
    lastPoint = point;
}

function finishStroke(e) {
    if (!painting) return;
    painting = false;
    if (e?.pointerId != null) svgEl.value?.releasePointerCapture?.(e.pointerId);
    const changed = store.endStroke();
    if (changed) {
        trackPaint(store.selectedPatternId, store.paintMode, changed, store.activeColor?.name);
    }
}

function onPointerLeave() {
    if (!painting) hoverTileId.value = null;
}

function onTileKeydown(e, tile) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (!store.activeColor) {
        showNeedsColorHint();
        return;
    }
    const changed = store.paintTile(tile.id);
    if (changed) {
        trackPaint(store.selectedPatternId, store.paintMode, changed, store.activeColor.name);
    }
}

function reset() {
    if (store.paintedCount === 0) return;
    store.resetColors();
    trackReset(store.selectedPatternId);
}

function changeRepeat(delta) {
    const before = store.repeat;
    store.setRepeat(before + delta);
    if (store.repeat !== before) trackRepeatChange(store.repeat);
}

onBeforeUnmount(() => clearTimeout(hintTimer));
</script>

<template>
    <main class="h-full min-h-0 flex flex-col gap-2 bg-white p-2 lg:p-4">
        <template v-if="pattern">
            <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 class="text-lg font-bold text-black mb-0">{{ pattern.name }}</h2>
                <span class="text-xs text-dryline">
                    {{ pattern.width }}×{{ pattern.height }}" sheet · {{ pattern.tileCount }} tiles
                    · {{ store.paintedCount }} painted
                </span>
                <ul
                    class="flex flex-wrap gap-1.5 text-[11px] text-dryline"
                    aria-label="Tiles per size"
                >
                    <li v-for="size in pattern.sizes" :key="size" class="px-2 py-0.5 bg-gray5new">
                        {{ pattern.tileCounts[size] }} × {{ formatSize(size) }}
                    </li>
                </ul>
            </div>

            <!-- Toolbar -->
            <!-- items-stretch: every gray group matches the tallest (two-line labels) -->
            <div
                class="flex flex-wrap items-stretch gap-2"
                role="toolbar"
                aria-label="Design tools"
            >
                <div class="flex items-start gap-2 bg-gray4new p-1.5 md:p-2">
                    <div class="flex flex-col items-center gap-1">
                        <button
                            type="button"
                            class="tool-btn"
                            aria-label="Undo"
                            title="Undo"
                            :disabled="!store.canUndo"
                            @click="store.undo()"
                        >
                            <ArrowUturnLeftIcon class="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <span class="tool-label">Undo</span>
                    </div>
                    <div class="flex flex-col items-center gap-1">
                        <button
                            type="button"
                            class="tool-btn"
                            aria-label="Redo"
                            title="Redo"
                            :disabled="!store.canRedo"
                            @click="store.redo()"
                        >
                            <ArrowUturnRightIcon class="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <span class="tool-label">Redo</span>
                    </div>
                    <div class="flex flex-col items-center gap-1">
                        <button
                            type="button"
                            class="tool-btn"
                            aria-label="Reset all tiles to unpainted"
                            title="Reset all tiles"
                            :disabled="store.paintedCount === 0"
                            @click="reset"
                        >
                            <ArrowPathIcon class="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <span class="tool-label">Reset</span>
                    </div>
                </div>

                <div
                    class="flex items-start gap-2 bg-gray4new p-1.5 md:p-2"
                    role="group"
                    aria-label="Paint mode"
                >
                    <div class="flex flex-col items-center gap-1">
                        <button
                            type="button"
                            class="tool-btn"
                            :class="{ 'tool-btn--active': store.paintMode === 'tile' }"
                            :aria-pressed="store.paintMode === 'tile' ? 'true' : 'false'"
                            title="Paint one tile at a time (drag to paint several)"
                            @click="store.setPaintMode('tile')"
                        >
                            <PaintBrushIcon class="w-4 h-4 md:w-5 md:h-5" />
                            <span class="sr-only">Paint tile</span>
                        </button>
                        <span class="tool-label">Paint<br />Tile</span>
                    </div>
                    <div class="flex flex-col items-center gap-1">
                        <button
                            type="button"
                            class="tool-btn"
                            :class="{ 'tool-btn--active': store.paintMode === 'fill' }"
                            :aria-pressed="store.paintMode === 'fill' ? 'true' : 'false'"
                            title="Paint every tile that shares the clicked tile's color"
                            @click="store.setPaintMode('fill')"
                        >
                            <Squares2X2Icon class="w-4 h-4 md:w-5 md:h-5" />
                            <span class="sr-only">Fill matching</span>
                        </button>
                        <span class="tool-label">Fill<br />Matching</span>
                    </div>
                </div>

                <div class="flex flex-col items-center gap-1 bg-gray4new p-1.5 md:p-2">
                    <div class="flex items-center gap-1">
                        <button
                            type="button"
                            class="tool-btn"
                            aria-label="Show fewer sheets"
                            title="Show fewer sheets"
                            :disabled="store.repeat <= MIN_REPEAT"
                            @click="changeRepeat(-1)"
                        >
                            <MinusIcon class="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <span class="text-sm w-10 text-center" aria-live="polite">
                            {{ store.repeat }}×{{ store.repeat }}
                        </span>
                        <button
                            type="button"
                            class="tool-btn"
                            aria-label="Show more sheets"
                            title="Show more sheets"
                            :disabled="store.repeat >= MAX_REPEAT"
                            @click="changeRepeat(1)"
                        >
                            <PlusIcon class="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                    </div>
                    <span class="tool-label">Sheets</span>
                </div>

                <div
                    class="flex items-center gap-2 bg-gray4new p-1.5 md:p-2 text-xs"
                    aria-live="polite"
                >
                    <template v-if="store.activeColor">
                        <span
                            class="inline-block w-6 h-6 border border-dryline"
                            :style="{ backgroundColor: store.activeColor.hex }"
                            aria-hidden="true"
                        />
                        <span class="text-black">
                            Painting with <strong>{{ store.activeColor.name }}</strong>
                        </span>
                        <button
                            type="button"
                            class="inline-flex items-center justify-center w-6 h-6 text-dryline hover:text-black"
                            aria-label="Put down the paint bucket"
                            title="Stop painting"
                            @click="store.setActiveColor(null)"
                        >
                            <XMarkIcon class="w-4 h-4" />
                        </button>
                    </template>
                    <span v-else class="text-dryline"
                        >Select a color in Step 2 to start painting</span
                    >
                </div>
            </div>

            <!-- Canvas -->
            <div class="relative flex-1 min-h-[280px] bg-engobe-100 p-2 lg:p-4">
                <svg
                    ref="svgEl"
                    class="w-full h-full block select-none"
                    :viewBox="`0 0 ${fieldWidth} ${fieldHeight}`"
                    preserveAspectRatio="xMidYMid meet"
                    role="group"
                    :aria-label="`${pattern.name} preview. ${store.activeColor ? 'Press Enter on a tile to paint it.' : 'Select a color to paint tiles.'}`"
                    :style="{
                        cursor: cursorStyle,
                        touchAction: store.activeColor ? 'none' : 'auto',
                    }"
                    data-testid="pattern-canvas"
                    @pointerdown="onPointerDown"
                    @pointermove="onPointerMove"
                    @pointerup="finishStroke"
                    @pointercancel="finishStroke"
                    @pointerleave="onPointerLeave"
                >
                    <rect :width="fieldWidth" :height="fieldHeight" :fill="store.groutColor?.hex" />
                    <g :id="sheetId">
                        <rect
                            v-for="t in pattern.tiles"
                            :key="t.id"
                            :data-tile-id="t.id"
                            :x="t.x + half"
                            :y="t.y + half"
                            :width="t.w - GROUT_WIDTH_IN"
                            :height="t.h - GROUT_WIDTH_IN"
                            :fill="store.tileFills[t.id] || UNPAINTED_FILL"
                            :stroke="store.tileFills[t.id] ? 'none' : UNPAINTED_STROKE"
                            stroke-width="1"
                            vector-effect="non-scaling-stroke"
                            tabindex="0"
                            role="button"
                            :aria-label="tileLabel(t)"
                            class="cm-tile"
                            @keydown="onTileKeydown($event, t)"
                            @focus="hoverTileId = t.id"
                            @blur="hoverTileId = null"
                        />
                    </g>
                    <use
                        v-for="(c, i) in copies.slice(1)"
                        :key="i"
                        :href="`#${sheetId}`"
                        :x="c.x"
                        :y="c.y"
                        aria-hidden="true"
                    />
                    <g pointer-events="none" aria-hidden="true">
                        <template v-for="(c, i) in copies" :key="`hl-${i}`">
                            <rect
                                v-for="t in highlightTiles"
                                :key="`hl-${i}-${t.id}`"
                                :x="c.x + t.x + half"
                                :y="c.y + t.y + half"
                                :width="t.w - GROUT_WIDTH_IN"
                                :height="t.h - GROUT_WIDTH_IN"
                                fill="none"
                                stroke="#423837"
                                stroke-width="2"
                                vector-effect="non-scaling-stroke"
                            />
                        </template>
                    </g>
                </svg>

                <div
                    v-if="needsColorHint"
                    class="absolute top-3 left-1/2 -translate-x-1/2 bg-dryline text-white text-sm px-4 py-2 shadow"
                    role="status"
                >
                    Choose a color in Step 2 first, then click a tile to paint it.
                </div>
            </div>

            <p class="text-xs text-dryline mb-0 min-h-[1rem]" aria-live="polite">
                <template v-if="hoverTile">
                    {{ formatSize(hoverTile.size) }} tile ·
                    {{ store.colors[hoverTile.id]?.name || 'Unpainted' }}
                </template>
            </p>

            <ShareDesign class="max-w-2xl" />
        </template>
        <div v-else class="text-center p-8 text-dryline">
            <p>Please select a pattern to begin.</p>
        </div>
    </main>
</template>

<style scoped>
.tool-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border: 1px solid #423837;
    background: #fff;
    color: #423837;
}
.tool-btn:hover:not(:disabled) {
    background: #423837;
    color: #fff;
}
.tool-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.tool-btn--active {
    background: #423837;
    color: #fff;
}
.tool-label {
    font-size: 10px;
    line-height: 1.1;
    text-align: center;
    color: #423837;
}
.cm-tile:focus {
    outline: none;
}
.cm-tile:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: -2px;
}
</style>
